import Container from '@risk-smart/themed-cloudscape-components/container';
import Icon from '@risk-smart/themed-cloudscape-components/icon';
import clsx from 'clsx';
import dayjs from 'dayjs';
import type { FC } from 'react';

import styles from './style.module.scss';
import type { NotificationItem } from './useNotificationItems';

type Props = {
  item: NotificationItem;
  onArchive: () => void;
  onRead: () => void;
};

/**
 * Split a notification message into its leading type label and the entity name —
 * e.g. "New Risk: Acme breach (R-7)" -> { label: "New Risk", name: "Acme breach (R-7)" }.
 * Works for both render paths (Knock templates + legacy handlers) since both emit
 * "<Type>: <name>". Messages without a "<type>: " prefix (e.g. "Document review due")
 * have no separate label — the whole string is the (bold) name.
 */
const splitMessage = (message: string): { label: string; name: string } => {
  const separator = message.indexOf(': ');
  if (separator === -1) {
    return { label: '', name: message };
  }

  return {
    label: message.slice(0, separator),
    name: message.slice(separator + 2),
  };
};

export const NotificationCell: FC<Props> = ({ item, onArchive, onRead }) => {
  const { label, name } = splitMessage(item.message ?? '');

  return (
    <div className={styles.cell}>
      <Container variant={'stacked'}>
        <div className={'flex flex-row'}>
          <div
            className={clsx('flex-none rounded-full h-4 w-4', {
              'bg-teal': !item.feedItem.read_at && item.url !== null,
              'bg-grey': !!item.feedItem.read_at || item.url === null,
            })}
          ></div>
          <div className={'grow px-2 cursor-pointer'} onClick={onRead}>
            {label && (
              <div className={'text-grey500 text-xs font-medium leading-4'}>
                {label}
              </div>
            )}
            {name && (
              <div className={'text-sm font-semibold leading-4 mb-1'}>
                {name}
              </div>
            )}

            <div className={'text-grey500 text-xs'}>{item.id}</div>

            <div className={'text-grey500 text-xs'}>
              {dayjs(item.feedItem.inserted_at).format('HH:mm, D MMM YYYY')}
            </div>
          </div>

          <span
            className={'cursor-pointer hover:opacity-50 text-grey500 -mt-1'}
            onClick={(e) => {
              onArchive();
              e.preventDefault();
            }}
          >
            <Icon name={'close'} size={'small'} />
          </span>
        </div>
      </Container>
    </div>
  );
};
